import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { Spinner } from './spinner.js'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'md' | 'sm'

/**
 * У основной кнопки текст цвета фона страницы, а не белый. В тёмной теме
 * акцент — светло-зелёный, и белые буквы на нём почти нечитаемы; `text-paper`
 * в светлой теме даёт почти белый, в тёмной — почти чёрный, и обе темы
 * проходят по контрасту от одного правила.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-paper border-accent hover:opacity-90',
  secondary: 'bg-raised text-ink border-line hover:bg-surface',
  ghost: 'bg-transparent text-ink-soft border-transparent hover:bg-line-soft',
  danger: 'bg-crimson-soft text-crimson border-crimson/30 hover:bg-crimson/15',
}

const SIZES: Record<Size, string> = {
  md: 'h-9 px-3.5 text-sm',
  sm: 'h-7 px-2.5 text-xs',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  /**
   * Показывает, что действие выполняется, и одновременно блокирует кнопку.
   * Один пропс вместо пары «loading + disabled» намеренно: разъехавшись,
   * эта пара даёт двойную отправку формы — самую частую ошибку в админках.
   */
  loading?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg border font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon}
      {children}
    </button>
  )
}
